import { isSupabaseConfigured, supabase } from "./supabaseClient";

export interface ManagedOffer {
  productKey: string;
  offerSku: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

export interface ManagedProduct {
  productKey: string;
  displayName: string;
  coreOfferSku: string;
  offers: ManagedOffer[];
}

export interface ManagedMembership {
  productKey: string;
  email: string;
  role: "buyer" | "admin";
  status: "active" | "suspended" | "invited";
  source: string;
}

export interface ManagedEntitlement {
  productKey: string;
  email: string;
  offerSku: string;
  active: boolean;
  source: string;
}

export interface AccessWorkspace {
  products: ManagedProduct[];
  memberships: ManagedMembership[];
  entitlements: ManagedEntitlement[];
}

export interface ManagedCustomer {
  email: string;
  memberships: ManagedMembership[];
  entitlements: ManagedEntitlement[];
}

function requireClient() {
  if (!supabase || !isSupabaseConfigured) {
    throw new Error("Supabase is not configured for this build.");
  }
  return supabase;
}

export function normalizeAccessEmail(email: string) {
  return email.trim().toLowerCase();
}

export function groupManagedCustomers(
  memberships: ManagedMembership[],
  entitlements: ManagedEntitlement[],
): ManagedCustomer[] {
  const customers = new Map<string, ManagedCustomer>();
  const ensureCustomer = (email: string) => {
    const normalizedEmail = normalizeAccessEmail(email);
    const existing = customers.get(normalizedEmail);
    if (existing) return existing;
    const customer: ManagedCustomer = {
      email: normalizedEmail,
      memberships: [],
      entitlements: [],
    };
    customers.set(normalizedEmail, customer);
    return customer;
  };

  for (const membership of memberships) {
    ensureCustomer(membership.email).memberships.push(membership);
  }
  for (const entitlement of entitlements) {
    ensureCustomer(entitlement.email).entitlements.push(entitlement);
  }

  return [...customers.values()].sort((left, right) =>
    left.email.localeCompare(right.email),
  );
}

async function currentActorId() {
  const client = requireClient();
  const { data, error } = await client.auth.getUser();
  if (error) throw error;
  if (!data.user) throw new Error("Your administrator session has expired.");
  return data.user.id;
}

export const accessRepository = {
  configured: isSupabaseConfigured,

  async loadWorkspace(adminEmail: string): Promise<AccessWorkspace> {
    const client = requireClient();
    const normalizedAdminEmail = normalizeAccessEmail(adminEmail);
    const adminMemberships = await client
      .from("product_memberships")
      .select("product_key")
      .eq("email", normalizedAdminEmail)
      .eq("role", "admin")
      .eq("status", "active");

    if (adminMemberships.error) throw adminMemberships.error;
    const productKeys = [
      ...new Set((adminMemberships.data ?? []).map((row) => row.product_key)),
    ];
    if (!productKeys.length) {
      return { products: [], memberships: [], entitlements: [] };
    }

    const [productsResult, offersResult, membershipsResult, entitlementsResult] =
      await Promise.all([
        client
          .from("app_products")
          .select("product_key, display_name, core_offer_sku")
          .in("product_key", productKeys)
          .eq("active", true)
          .order("display_name"),
        client
          .from("product_offers")
          .select("product_key, offer_sku, label, sort_order, active")
          .in("product_key", productKeys)
          .order("sort_order"),
        client
          .from("product_memberships")
          .select("product_key, email, role, status, source")
          .in("product_key", productKeys)
          .order("email"),
        client
          .from("product_entitlements")
          .select("product_key, email, offer_sku, active, source")
          .in("product_key", productKeys)
          .order("email"),
      ]);

    const firstError = [
      productsResult.error,
      offersResult.error,
      membershipsResult.error,
      entitlementsResult.error,
    ].find(Boolean);
    if (firstError) throw firstError;

    const offers = (offersResult.data ?? []).map((row) => ({
      productKey: row.product_key,
      offerSku: row.offer_sku,
      label: row.label,
      sortOrder: row.sort_order,
      active: row.active,
    }));

    return {
      products: (productsResult.data ?? []).map((product) => ({
        productKey: product.product_key,
        displayName: product.display_name,
        coreOfferSku: product.core_offer_sku,
        offers: offers.filter(
          (offer) => offer.productKey === product.product_key && offer.active,
        ),
      })),
      memberships: (membershipsResult.data ?? []).map((row) => ({
        productKey: row.product_key,
        email: row.email,
        role: row.role as ManagedMembership["role"],
        status: row.status as ManagedMembership["status"],
        source: row.source,
      })),
      entitlements: (entitlementsResult.data ?? []).map((row) => ({
        productKey: row.product_key,
        email: row.email,
        offerSku: row.offer_sku,
        active: row.active,
        source: row.source,
      })),
    };
  },

  async grantOffer(productKey: string, email: string, offerSku: string) {
    const client = requireClient();
    const actorId = await currentActorId();
    const targetEmail = normalizeAccessEmail(email);
    const existingMembership = await client
      .from("product_memberships")
      .select("role")
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .maybeSingle();
    if (existingMembership.error) throw existingMembership.error;

    const membership = await client.from("product_memberships").upsert(
      {
        product_key: productKey,
        email: targetEmail,
        role:
          existingMembership.data?.role === "admin" ? "admin" : "buyer",
        status: "active",
        source: "manual",
        granted_by: actorId,
      },
      { onConflict: "product_key,email" },
    );
    if (membership.error) throw membership.error;

    const entitlement = await client.from("product_entitlements").upsert(
      {
        product_key: productKey,
        email: targetEmail,
        offer_sku: offerSku,
        active: true,
        source: "manual",
        granted_by: actorId,
      },
      { onConflict: "product_key,email,offer_sku" },
    );
    if (entitlement.error) throw entitlement.error;
  },

  async revokeOffer(productKey: string, email: string, offerSku: string) {
    const client = requireClient();
    const targetEmail = normalizeAccessEmail(email);
    const { error } = await client
      .from("product_entitlements")
      .update({ active: false, source: "manual" })
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .eq("offer_sku", offerSku);
    if (error) throw error;

    const remaining = await client
      .from("product_entitlements")
      .select("id")
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .eq("active", true)
      .limit(1);
    if (remaining.error) throw remaining.error;
    if (remaining.data?.length) return;

    const membership = await client
      .from("product_memberships")
      .update({ status: "suspended", source: "manual" })
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .eq("role", "buyer");
    if (membership.error) throw membership.error;
  },
};
